/**
 * BioChat UI latency instrumentation.
 *
 * One place for the numbers the chat-workspace budgets are written against:
 * click-to-paint per chat switch, omics requests per minute (by endpoint
 * group), and component render counts. Read them live from the console with
 * ``window.__omicsPerf.snapshot()``; switch timings also ship to Tempo.
 */

import { emitDebugSessionLog } from './debug-session-log';

const REQUEST_WINDOW_MS = 60_000;
const MAX_TRACKED_REQUESTS = 2_000;

/** Ordered: first match wins. */
const URL_GROUPS = [
  ['snapshot', '/snapshot/'],
  ['outputs', '/outputs'],
  ['stream-status', '/stream-status'],
  ['containers', '/containers'],
  ['sessions', '/sessions'],
  ['predict-activity', '/predict-activity'],
  ['plans', '/plans/'],
  ['storage-index', '/storage/index'],
  ['code', '/code'],
  ['jobs', '/jobs'],
];

let requests = [];
let switchStarts = new Map();
let lastSwitch = null;
const renders = {};

export function classifyOmicsUrl(url) {
  const raw = String(url || '');
  for (const [group, needle] of URL_GROUPS) {
    if (raw.includes(needle)) return group;
  }
  return 'other';
}

function pruneRequests(now) {
  const cutoff = now - REQUEST_WINDOW_MS;
  if (requests.length && requests[0].t < cutoff) {
    requests = requests.filter((row) => row.t >= cutoff);
  }
  if (requests.length > MAX_TRACKED_REQUESTS) {
    requests = requests.slice(-MAX_TRACKED_REQUESTS);
  }
}

export function recordOmicsRequest(method, url, now = Date.now()) {
  const m = String(method || 'GET').toUpperCase();
  requests.push({ t: now, method: m, group: classifyOmicsUrl(url) });
  pruneRequests(now);
}

/** Requests in the trailing minute: ``{ total, gets, byGroup }``. */
export function omicsRequestStats(now = Date.now()) {
  pruneRequests(now);
  const byGroup = {};
  let gets = 0;
  requests.forEach((row) => {
    byGroup[row.group] = (byGroup[row.group] || 0) + 1;
    if (row.method === 'GET') gets += 1;
  });
  return { total: requests.length, gets, byGroup };
}

export function markChatSwitchStart(chatId, now = performance.now()) {
  const id = String(chatId || '').trim();
  if (!id) return;
  switchStarts.set(id, now);
}

/**
 * Close a switch timing. ``source`` is ``cache`` (store hit) or ``network``.
 * Returns elapsed ms, or null when no switch was started for this chat.
 */
export function markChatPainted(chatId, { source = 'network' } = {}, now = performance.now()) {
  const id = String(chatId || '').trim();
  const started = switchStarts.get(id);
  if (!id || started === undefined) return null;
  switchStarts.delete(id);
  const ms = Math.max(0, Math.round(now - started));
  lastSwitch = { chatId: id, ms, source };
  emitDebugSessionLog({
    location: 'omics-perf.js:markChatPainted',
    message: 'fe_chat_switch_paint',
    hypothesisId: 'PERF',
    data: { chat_id: id, click_to_paint_ms: ms, source },
  });
  return ms;
}

export function countRender(name) {
  renders[name] = (renders[name] || 0) + 1;
  return renders[name];
}

export function renderCounts() {
  return { ...renders };
}

export function omicsPerfSnapshot(now = Date.now()) {
  return {
    requests: omicsRequestStats(now),
    lastSwitch,
    renders: renderCounts(),
  };
}

export function __resetOmicsPerfForTests() {
  requests = [];
  switchStarts = new Map();
  lastSwitch = null;
  Object.keys(renders).forEach((key) => delete renders[key]);
}

if (typeof window !== 'undefined') {
  window.__omicsPerf = {
    snapshot: omicsPerfSnapshot,
    requests: omicsRequestStats,
    renders: renderCounts,
  };
}
